/**
 * Goal 1→2 handoff: create_reports (multi-sub) as one dialog-owned operation.
 * Dialog owns feedback — no parallel success/error toast for the same op.
 * 422 atomic failure → NOT_APPLIED (no partial-success presentation).
 */
import axios from "../../../../api/axios";
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  canConfirmStepFromEvidence,
  outcomeAfterRefreshFailure,
} from "../../../../operationFeedback";
import { isCompleteDeliveryAddress } from "../../../../constants/countries";

export const CREATE_REPORTS_OPERATION_KEY = "create_reports";

export function evaluateCreateReportsAddressPreflight({
  agreement,
  selectedSubs,
  subs,
}) {
  const selected = (subs || []).filter((sub) =>
    (selectedSubs || []).includes(sub.id)
  );
  if (!selectedSubs?.length) {
    return { ready: false, reason: "no_selection", invalidSub: null };
  }
  const invalid = selected.find((sub) => {
    const address = sub.delivery_address?.id
      ? sub.delivery_address
      : agreement?.delivery_address;
    return !isCompleteDeliveryAddress(address);
  });
  if (invalid) {
    return { ready: false, reason: "invalid_address", invalidSub: invalid };
  }
  return { ready: true, reason: null, invalidSub: null };
}

function defaultExecutionSteps(t) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.create_reports.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "create",
      label: t("operation_feedback.create_reports.step.create"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "refresh",
      label: t("operation_feedback.create_reports.step.refresh"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

function reportLinksFromPayload(payload, t) {
  const rows = payload?.reports || payload?.data?.reports || [];
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r?.id)
    .map((r) => ({
      href: `/dashboard/service/reports/${r.id}`,
      label:
        r.displayTitle ||
        r.report_number ||
        t("operation_feedback.create_reports.open_report", { id: r.id }),
    }));
}

/**
 * @param {object} args
 * @param {object} args.agreement
 * @param {number[]} args.selectedSubs
 * @param {object[]} args.subs
 * @param {object} args.settings - create_reports settings payload
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onRefresh]
 * @param {Function} args.t
 */
export async function runCreateReportsOperation({
  agreement,
  selectedSubs,
  subs,
  settings,
  companyId,
  onRefresh,
  t,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = agreement?.id;
  const title = t("operation_feedback.create_reports.title", {
    count: selectedSubs?.length || 0,
  });

  const op = coordinator.start({
    operationKey: CREATE_REPORTS_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateCreateReportsAddressPreflight({
    agreement,
    selectedSubs,
    subs,
  });

  // Readiness must never count as confirmed execution success.
  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional gate */
  }

  if (!preflight.ready) {
    const detail =
      preflight.reason === "invalid_address"
        ? t("service.agreements.actions.err_invalid_address")
        : t("operation_feedback.create_reports.preflight_no_selection");
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "create", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.create_reports.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "create", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let response;
  try {
    response = await axios.post(
      `/service/agreements/${agreement.id}/create_reports`,
      {
        sub_ids: selectedSubs,
        settings,
      }
    );
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: CREATE_REPORTS_OPERATION_KEY,
    });
    const status = err?.response?.status;
    const isAtomic422 = status === 422 && err?.response?.data?.atomic === true;
    // Older APIs may have committed earlier reports before returning 422.
    // Only an explicit rollback guarantee or problem outcome is authoritative.
    const outcome = isAtomic422
      ? OUTCOME.NOT_APPLIED
      : err?.response?.data?.problem?.outcome || OUTCOME.UNKNOWN;
    coordinator.updateStep(id, "create", {
      status: outcome === OUTCOME.UNKNOWN ? STEP_STATUS.UNCONFIRMED : STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: isAtomic422
        ? t("operation_feedback.create_reports.atomic_failed", {
            defaultValue: normalized.safeMessage,
          })
        : normalized.safeMessage,
      outcome,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = response?.data || {};
  if (
    response?.status >= 400 ||
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true
  ) {
    const normalized = normalizeError(
      { response: { status: response?.status || 422, data: payload } },
      { operationKey: CREATE_REPORTS_OPERATION_KEY }
    );
    coordinator.updateStep(id, "create", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  const count = payload.reports_created ?? payload.reports?.length ?? 0;
  const links = reportLinksFromPayload(payload, t);

  coordinator.updateStep(id, "create", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.create_reports.create_ok", { count }),
    resultRef: { reports_created: count },
    revision: rev++,
  });

  if (typeof onRefresh === "function") {
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await Promise.resolve(onRefresh(agreement));
      coordinator.updateStep(id, "refresh", {
        status: STEP_STATUS.COMPLETED,
        revision: rev++,
      });
      const afterOk = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, false);
      coordinator.complete(id, {
        summary: t("operation_feedback.create_reports.success", { count }),
        outcome: afterOk.outcome || OUTCOME.APPLIED,
        links,
      });
      return { clientOperationId: id, ok: true, payload };
    } catch (refreshErr) {
      const normalized = normalizeError(refreshErr, {
        operationKey: CREATE_REPORTS_OPERATION_KEY,
      });
      const afterFail = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, true);
      coordinator.updateStep(id, "refresh", {
        status: afterFail.refreshStatus || STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
      coordinator.complete(id, {
        summary: t(
          afterFail.summaryKey || "operation_feedback.saved_list_refresh_failed"
        ),
        outcome: afterFail.outcome || OUTCOME.PARTIAL,
        links,
      });
      return { clientOperationId: id, ok: true, payload, refreshFailed: true };
    }
  }

  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_RELEVANT,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary: t("operation_feedback.create_reports.success", { count }),
    outcome: OUTCOME.APPLIED,
    links,
  });
  return { clientOperationId: id, ok: true, payload };
}
